import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ArrowRight, Clock } from 'lucide-react';

export const Resources = () => {
  const articles = [
    {
      title: 'What Is GEO (Generative Engine Optimization) and Why It Matters in 2025',
      excerpt: 'Learn how Generative Engine Optimization is reshaping digital discovery and why traditional SEO alone is no longer enough.',
      category: 'GEO/AIO',
      readTime: '8 min read',
      date: 'Jan 15, 2025',
      image: 'https://images.unsplash.com/photo-1745674684539-d90293d659a9?w=600&h=400&fit=crop'
    },
    {
      title: 'How ChatGPT, Perplexity, and Google AI Overviews Choose Their Sources',
      excerpt: 'Understand the algorithms and signals that AI engines use to determine which brands to cite and recommend.',
      category: 'AI Search',
      readTime: '10 min read',
      date: 'Jan 10, 2025',
      image: 'https://images.unsplash.com/photo-1745674684468-b9fc392fda3f?w=600&h=400&fit=crop'
    },
    {
      title: 'GEO vs. Traditional SEO: What Changes and What Stays the Same',
      excerpt: 'A comprehensive comparison of GEO and SEO strategies, and how to integrate both for maximum visibility.',
      category: 'Strategy',
      readTime: '12 min read',
      date: 'Jan 5, 2025',
      image: 'https://images.unsplash.com/photo-1761305135319-70b8dbde3525?w=600&h=400&fit=crop'
    },
    {
      title: 'Preparing Your Brand for ChatGPT Ads and AI-Native Ad Platforms',
      excerpt: 'OpenAI has confirmed ads are coming to ChatGPT. Here\'s how to prepare your brand for this new advertising landscape.',
      category: 'AI Ads',
      readTime: '7 min read',
      date: 'Dec 28, 2024',
      image: 'https://images.unsplash.com/photo-1597733336794-12d05021d510?w=600&h=400&fit=crop'
    },
    {
      title: 'AI Agents for Lead Generation: A Practical Introduction',
      excerpt: 'How to use AI agents to automatically capture, qualify, and nurture leads from AI-driven traffic.',
      category: 'AI Agents',
      readTime: '9 min read',
      date: 'Dec 20, 2024',
      image: 'https://images.unsplash.com/photo-1581090464777-f3220bbe1b8b?w=600&h=400&fit=crop'
    },
    {
      title: 'The Complete Guide to Schema Markup for AI Engines',
      excerpt: 'Learn how to implement structured data that AI engines can easily understand and cite.',
      category: 'Technical',
      readTime: '15 min read',
      date: 'Dec 15, 2024',
      image: 'https://images.unsplash.com/photo-1531539427495-97c44a449837?w=600&h=400&fit=crop'
    }
  ];

  const categories = ['All', 'GEO/AIO', 'AI Search', 'AI Ads', 'AI Agents', 'Strategy', 'Technical'];

  return (
    <div className="min-h-screen pb-20">
      {/* Header */}
      <section className="pt-24 lg:pt-32 pb-12 lg:pb-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold mb-6">
            Blog on <span className="text-gradient">AI Search & GEO/AIO</span>
          </h1>
          <p className="text-lg sm:text-xl text-muted-foreground max-w-3xl mx-auto">
            Articles, breakdowns, and best practices for dominating AI-driven search and discovery.
          </p>
        </div>
      </section>

      {/* Categories */}
      <section className="py-8 border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-2 justify-center">
            {categories.map((category) => (
              <Badge
                key={category}
                variant={category === 'All' ? 'default' : 'outline'}
                className="cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors"
              >
                {category}
              </Badge>
            ))}
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {articles.map((article, index) => (
              <Card key={index} className="border-border hover:shadow-lg transition-all group overflow-hidden flex flex-col">
                <div className="overflow-hidden">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <CardHeader className="flex-1">
                  <div className="flex items-center justify-between mb-3">
                    <Badge variant="secondary">{article.category}</Badge>
                    <div className="flex items-center text-xs text-muted-foreground">
                      <Clock className="w-3 h-3 mr-1" />
                      {article.readTime}
                    </div>
                  </div>
                  <CardTitle className="font-display text-xl mb-2 group-hover:text-primary transition-colors">
                    {article.title}
                  </CardTitle>
                  <CardDescription className="text-sm">{article.excerpt}</CardDescription>
                </CardHeader>
                <CardContent className="mt-auto">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">{article.date}</span>
                    <Button variant="ghost" size="sm" className="group-hover:text-primary">
                      Read More
                      <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="py-20 bg-muted/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-display font-bold mb-6">
            Stay Updated on AI Search Trends
          </h2>
          <p className="text-lg text-muted-foreground mb-8">
            Get weekly insights on GEO/AIO strategies, AI engine updates, and case studies delivered to your inbox.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
            <input
              type="email"
              placeholder="Enter your email"
              className="flex-1 px-4 py-3 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <Button size="lg" className="gradient-primary text-primary-foreground shadow-primary">
              Subscribe
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-4">
            No spam, unsubscribe anytime
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-display font-bold mb-6">
            Ready to Apply These Strategies?
          </h2>
          <p className="text-lg text-muted-foreground mb-8">
            Get a free GEO/AIO audit to see where your brand stands and what opportunities you're missing.
          </p>
          <Link to="/free-audit">
            <Button size="lg" className="gradient-primary text-primary-foreground shadow-primary">
              Get My Free Audit
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};
